import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { usePlacePayments, useSetPlacePayments } from '../../hooks/queries/usePlacePayments';
import { usePaymentsAll } from '../../hooks/queries/usePayments';
import useSnackbar from '../../hooks/useSnackbar';
import PaymentMethodsManagerDialog from '../payments/PaymentMethodsManagerDialog';

/**
 * PlacePaymentsManagerDialog
 *
 * Dialog para gerenciar quais formas de pagamento um local aceita.
 * Permite marcar/desmarcar formas de pagamento e salva as alterações.
 *
 * @param {Boolean} open - Se o diálogo está aberto
 * @param {Function} onClose - Callback ao fechar
 * @param {Object} place - Local sendo gerenciado
 * @param {Function} onOpenCascadeConfirm - Callback para abrir diálogo de confirmação de cascata
 */
export default function PlacePaymentsManagerDialog({
  open,
  onClose,
  place,
  onOpenCascadeConfirm,
}) {
  const { data: allPayments = [], isLoading: loadingPayments } = usePaymentsAll({
    enabled: open
  });
  const { data: placePayments = [], isLoading: loadingPlacePayments } = usePlacePayments(
    place?.id,
    { enabled: !!place?.id && open }
  );
  const setPlacePayments = useSetPlacePayments(place?.id);

  const [selectedPaymentIds, setSelectedPaymentIds] = useState(new Set());
  const [initialPaymentIds, setInitialPaymentIds] = useState(new Set());
  const [paymentsManagerOpen, setPaymentsManagerOpen] = useState(false);
  const { showSuccess, showError } = useSnackbar();

  // Sincronizar state com dados carregados
  useEffect(() => {
    if (open && placePayments && Array.isArray(placePayments)) {
      const paymentIds = new Set(placePayments.map(pp => pp.paymentId));
      setSelectedPaymentIds(paymentIds);
      setInitialPaymentIds(new Set(paymentIds));
    }
  }, [open, placePayments]);

  const handleTogglePayment = (paymentId) => {
    setSelectedPaymentIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(paymentId)) {
        newSet.delete(paymentId);
      } else {
        newSet.add(paymentId);
      }
      return newSet;
    });
  };

  const hasChanges = useMemo(() => {
    if (initialPaymentIds.size !== selectedPaymentIds.size) return true;
    for (const id of selectedPaymentIds) {
      if (!initialPaymentIds.has(id)) return true;
    }
    return false;
  }, [initialPaymentIds, selectedPaymentIds]);

  const handleClose = () => {
    onClose();
  };

  const handleSave = async () => {
    try {
      await setPlacePayments.mutateAsync({
        paymentIds: Array.from(selectedPaymentIds),
        confirmCascade: false,
      });
      showSuccess('Formas de pagamento atualizadas com sucesso!');
      handleClose();
    } catch (error) {
      // Verificar se é erro 409 (cascata necessária)
      if (error.response?.status === 409 && error.response?.data?.cascade) {
        // Abrir diálogo de confirmação de cascata
        if (onOpenCascadeConfirm) {
          onOpenCascadeConfirm({
            cascades: error.response.data.cascade,
            onConfirm: handleConfirmCascade,
          });
        }
      } else {
        showError(
          error.response?.data?.message ||
          'Erro ao atualizar formas de pagamento'
        );
      }
    }
  };

  const handleConfirmCascade = async () => {
    try {
      await setPlacePayments.mutateAsync({
        paymentIds: Array.from(selectedPaymentIds),
        confirmCascade: true,
      });
      showSuccess('Formas de pagamento atualizadas com sucesso!');
      handleClose();
    } catch (error) {
      showError(
        error.response?.data?.message ||
        'Erro ao atualizar formas de pagamento'
      );
    }
  };

  const isLoading = loadingPayments || loadingPlacePayments || setPlacePayments.isPending;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PaymentIcon />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Formas de pagamento aceitas — {place?.name}
            </Typography>
            <Tooltip title="Gerenciar catálogo de formas de pagamento">
              <IconButton
                onClick={() => setPaymentsManagerOpen(true)}
                size="small"
                disabled={isLoading}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : allPayments.length === 0 ? (
            <Alert severity="info">
              Nenhuma forma de pagamento disponível.
              <Box mt={1}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setPaymentsManagerOpen(true)}
                  variant="text"
                  size="small"
                >
                  Adicionar forma de pagamento
                </Button>
              </Box>
            </Alert>
          ) : (
            <>
              <List>
                {allPayments.map((payment) => (
                  <ListItem
                    key={payment.id}
                    divider
                    sx={{
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      mb: 1,
                      pl: 1,
                      pr: 2,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Checkbox
                        checked={selectedPaymentIds.has(payment.id)}
                        onChange={() => handleTogglePayment(payment.id)}
                        disabled={isLoading}
                      />
                    </ListItemIcon>
                    {payment.icon && (
                      <ListItemIcon sx={{ minWidth: 40, ml: -1 }}>
                        <Typography variant="h6">{payment.icon}</Typography>
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={payment.name}
                      secondary={
                        payment.type && (
                          <Tooltip title={`Tipo: ${payment.type}`}>
                            <Typography variant="caption" color="text.secondary">
                              {payment.type}
                            </Typography>
                          </Tooltip>
                        )
                      }
                    />
                  </ListItem>
                ))}
              </List>

              {/* Adicionar nova forma de pagamento */}
              <Box
                mt={2}
                pt={2}
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setPaymentsManagerOpen(true)}
                  fullWidth
                  variant="outlined"
                  disabled={isLoading}
                >
                  Adicionar nova forma de pagamento
                </Button>
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!hasChanges || isLoading}
          >
            {setPlacePayments.isPending ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Salvando...
              </>
            ) : (
              'Salvar'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Methods Manager Dialog */}
      <PaymentMethodsManagerDialog
        open={paymentsManagerOpen}
        onClose={() => setPaymentsManagerOpen(false)}
      />
    </>
  );
}

PlacePaymentsManagerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  place: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  onOpenCascadeConfirm: PropTypes.func,
};
